import _ from "underscore";
import { Action, Reducer } from "redux";
import { Draft, produce } from "immer";
import { RunningProcessStatus } from "avvir";

import Pipeline from "../../models/domain/pipeline";
import RunningProcess from "../../models/domain/running_process";
import { EXPORT_BATCH_IFC_PROCESS_CHECKED, ExportBatchIfcProcessCheckedEvent } from "../../events/export_batch_ifc_process_checked";
import { EXPORT_BATCH_IFC_TRIGGERED, ExportBatchIfcTriggeredEvent } from "../../events/export_batch_ifc_triggered";
import { EXPORT_IFC_PROCESS_CHECKED, ExportIfcProcessCheckedEvent } from "../../events/export_ifc_process_checked";
import { EXPORT_IFC_TRIGGERED, ExportIfcTriggeredEvent } from "../../events/export_ifc_triggered";
import { FILE_UPLOAD_PROGRESSED, FileUploadProgressedEvent } from "../../events/files/upload/file_upload_progressed";
import { PIPELINE_RUNNING_PROCESS_CHECKED, PipelineRunningProcessCheckedEvent } from "../../events/pipeline_running_process_checked";
import { PIPELINE_RUNNING_PROCESS_TRIGGERED, PipelineRunningProcessTriggeredEvent } from "../../events/pipeline_running_process_triggered";
import { PIPELINES_FOR_FLOOR_LOADED, PipelinesForFloorLoadedEvent } from "../../events/loaded/floors/pipelines_for_floor_loaded";
import { PROJECT_PIPELINES_LOADED, ProjectPipelinesLoadedEvent } from "../../events/loaded/projects/project_pipelines_loaded";
import { ProcessTypes, RunningProcessWithId, RunningProcessWithName } from "../../models/domain/process";
import { removePipelineFromStorage, writePipeline } from "../utilities/store_in_local_storage";
import { RUNNING_PROCESS_CHECKED, RunningProcessCheckedEvent } from "../../events/running_process/running_process_checked";
import { RUNNING_PROCESS_FAILED, RunningProcessFailedEvent } from "../../events/running_process/running_process_failed";
import { RUNNING_PROCESS_STARTED, RunningProcessStartedEvent } from "../../events/running_process/running_process_started";
import { RUNNING_PROCESS_SUCCEEDED, RunningProcessSucceededEvent } from "../../events/running_process/running_process_succeeded";
import { UPDATE_FLOOR_MASTERFORMAT_PROGRESS_COMPLETED, UpdateFloorMasterformatProgressCompletedEvent } from "../../events/update_floor_masterformat_progress_completed";
import { UPDATE_FLOOR_MASTERFORMAT_PROGRESS_STARTED, UpdateFloorMasterformatProgressStartedEvent } from "../../events/update_floor_masterformat_progress_started";
import { UPDATE_PROJECT_MASTERFORMAT_PROGRESS_COMPLETED, UpdateProjectMasterformatProgressCompletedEvent } from "../../events/update_project_masterformat_progress_completed";
import { UPDATE_PROJECT_MASTERFORMAT_PROGRESS_STARTED, UpdateProjectMasterformatProgressStartedEvent } from "../../events/update_project_masterformat_progress_started";

import type { ByDatabaseId } from "type_aliases";

type RunningProcessesEvents =
  | ExportIfcTriggeredEvent
  | ExportBatchIfcTriggeredEvent
  | ExportIfcProcessCheckedEvent
  | ExportBatchIfcProcessCheckedEvent
  | PipelinesForFloorLoadedEvent
  | ProjectPipelinesLoadedEvent
  | PipelineRunningProcessTriggeredEvent
  | PipelineRunningProcessCheckedEvent
  | FileUploadProgressedEvent
  | UpdateProjectMasterformatProgressStartedEvent
  | UpdateProjectMasterformatProgressCompletedEvent
  | UpdateFloorMasterformatProgressStartedEvent
  | UpdateFloorMasterformatProgressCompletedEvent
  | RunningProcessStartedEvent
  | RunningProcessCheckedEvent
  | RunningProcessFailedEvent
  | RunningProcessSucceededEvent
  | Action<"update_selected_elements_status_started">
  | Action<"update_selected_elements_status_done">
  | Action<"update_selected_elements_status_failed">

type FileUploadStatus = { fileTitle: string, isUploading: boolean, progress: number };

export interface RunningProcessesStore {
  uploads?: { [fileName: string]: FileUploadStatus };
  byId: ByDatabaseId<Pipeline>;
  byRunningProcessId: ByDatabaseId<RunningProcess>;
  exports: ProcessTypes[];
  isSelectedElementsUpdating: boolean;
}

const beforeUnloadEventHandler = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "Do you want to leave this site? Changes you made may not be saved.";
};

const updateOrRemoveExportProcess = function updateOrRemoveProcess(runningProcesses: Draft<RunningProcessesStore>, processIndex: number, processStatus: "running" | "complete" | "failed") {
  if (processIndex !== -1) {
    if (processStatus === "running") {
      runningProcesses.exports[processIndex].updates += 1;
    } else {
      runningProcesses.exports.splice(processIndex, 1);
    }
  }
};

type ProcessEvent = PipelineRunningProcessCheckedEvent
                    | ExportIfcProcessCheckedEvent
                    | UpdateProjectMasterformatProgressCompletedEvent
                    | UpdateFloorMasterformatProgressCompletedEvent
                    | ExportBatchIfcProcessCheckedEvent;

const checkProcess = (runningProcessesDraft: Draft<RunningProcessesStore>, event: ProcessEvent) => {
  switch (event.type) {
    case EXPORT_BATCH_IFC_PROCESS_CHECKED:
    case EXPORT_IFC_PROCESS_CHECKED: {
      const payload = event.payload;
      const checkedIndex = _(runningProcessesDraft.exports).findIndex({ processName: payload.processName });
      updateOrRemoveExportProcess(runningProcessesDraft, checkedIndex, event.payload.status);
      break;
    }
    case UPDATE_PROJECT_MASTERFORMAT_PROGRESS_COMPLETED: {
      const payload = event.payload;
      const checkedIndex = _(runningProcessesDraft.exports).findIndex({ processId: payload.processId });
      updateOrRemoveExportProcess(runningProcessesDraft, checkedIndex, "complete");
      break;
    }
    case PIPELINE_RUNNING_PROCESS_CHECKED: {
      if (event.payload.status === RunningProcessStatus.FAILED
          || event.payload.status === RunningProcessStatus.COMPLETED
          || !event.payload.id) {
        removePipelineFromStorage(event.payload.id);
      } else {
        writePipeline(event.payload);
      }

      if (event.payload.id) {
        const process = runningProcessesDraft.byId[event.payload.id];
        runningProcessesDraft.byId[event.payload.id] = new Pipeline(event.payload);
        runningProcessesDraft.byId[event.payload.id].updates = (process?.updates || 0) + 1;
        // Status-check payloads drop the trigger args; carry them forward so Retry keeps working.
        runningProcessesDraft.byId[event.payload.id].retryArguments = (event.payload as any).retryArguments ?? process?.retryArguments ?? null;
      }
    }
  }
};

const defaultRunningProcesses: RunningProcessesStore = {
  exports: [],
  byId: {},
  byRunningProcessId: {},
  isSelectedElementsUpdating: false,
};

type RunningProcessesReducer = Reducer<RunningProcessesStore, RunningProcessesEvents>

const reduceRunningProcesses: RunningProcessesReducer = (runningProcesses = defaultRunningProcesses, event) => {
  return produce<RunningProcessesStore>(runningProcesses, (runningProcessesDraft) => {
    runningProcessesDraft.exports = runningProcessesDraft.exports || [];
    runningProcessesDraft.byId = runningProcessesDraft.byId || {};
    runningProcessesDraft.byRunningProcessId = runningProcessesDraft.byRunningProcessId || {};
    runningProcessesDraft.uploads = runningProcessesDraft.uploads || {};

    switch (event?.type) {
      case "update_selected_elements_status_started": {
        runningProcessesDraft.isSelectedElementsUpdating = true;
        break;
      }
      case "update_selected_elements_status_failed":
      case "update_selected_elements_status_done": {
        runningProcessesDraft.isSelectedElementsUpdating = false;
        break;
      }
      case EXPORT_IFC_TRIGGERED: {
        runningProcessesDraft.exports.push(new RunningProcessWithName({
          ...event.payload,
          name: "Ifc Export Processing...",
          updates: 0
        }));
        break;
      }
      case EXPORT_BATCH_IFC_TRIGGERED: {
        runningProcessesDraft.exports.push(new RunningProcessWithName({
          ...event.payload,
          name: "Batch Ifc Export Processing...",
          updates: 0
        }));
        break;
      }
      case EXPORT_IFC_PROCESS_CHECKED: {
        checkProcess(runningProcessesDraft, event);
        break;
      }
      case RUNNING_PROCESS_STARTED:
      case RUNNING_PROCESS_FAILED:
      case RUNNING_PROCESS_SUCCEEDED:
      case RUNNING_PROCESS_CHECKED: {
        runningProcessesDraft.byRunningProcessId[event.payload.runningProcess.id] = new RunningProcess(event.payload.runningProcess);
        break;
      }
      case PIPELINE_RUNNING_PROCESS_TRIGGERED: {
        if (event.payload.status === RunningProcessStatus.RUNNING && event.payload.id) {
          writePipeline(event.payload);
        }
        runningProcessesDraft.byId[event.payload.id] = new Pipeline(event.payload);
        break;
      }
      case UPDATE_PROJECT_MASTERFORMAT_PROGRESS_STARTED: {
        runningProcessesDraft.exports.push(new RunningProcessWithId({
          ...event.payload,
          name: "Updating 4D Progress...",
          updates: 0
        }));
        break;
      }
      case UPDATE_PROJECT_MASTERFORMAT_PROGRESS_COMPLETED: {
        checkProcess(runningProcessesDraft, event);
        break;
      }
      case UPDATE_FLOOR_MASTERFORMAT_PROGRESS_STARTED: {
        runningProcessesDraft.exports.push(new RunningProcessWithId({
          ...event.payload,
          name: "Updating Floor 4D Progress...",
          updates: 0
        }));
        break;
      }
      case UPDATE_FLOOR_MASTERFORMAT_PROGRESS_COMPLETED:
      case PIPELINE_RUNNING_PROCESS_CHECKED: {
        checkProcess(runningProcessesDraft, event);
        break;
      }
      case PIPELINES_FOR_FLOOR_LOADED: {
        event.payload.pipelines.forEach(pipeline => {
          runningProcessesDraft.byId[pipeline.id] = new Pipeline(pipeline);
        });
        break;
      }
      case PROJECT_PIPELINES_LOADED: {
        event.payload.pipelines.forEach(pipeline => {
          runningProcessesDraft.byId[pipeline.id] = new Pipeline(pipeline);
        });
        break;
      }
      case FILE_UPLOAD_PROGRESSED: {
        runningProcessesDraft.uploads[event.payload.fileTitle] = event.payload;
        for (const upload of Object.values(runningProcessesDraft.uploads)) {
          if (upload.isUploading) {
            window.addEventListener("beforeunload", beforeUnloadEventHandler);
            break;
          } else {
            window.removeEventListener("beforeunload", beforeUnloadEventHandler);
          }
        }
        break;
      }
      default:
    }
  });
};


export default reduceRunningProcesses;
